import { Link } from 'react-router-dom'
import { Droplets, MapPin, Phone, Mail, ExternalLink } from 'lucide-react'
import { company, navigation } from '@data/company'

export default function Footer() {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="relative bg-martimma-dark-light border-t border-white/5">
      {/* Top decorative line */}
      <div className="h-1 bg-gradient-to-r from-martimma-red via-martimma-yellow to-martimma-red" />

      <div className="section-padding max-w-7xl mx-auto py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <Droplets className="w-8 h-8 text-martimma-red" />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-white">MARTIMMA</span>
                <span className="text-[10px] text-martimma-yellow tracking-[0.2em]">GLOBAL SERVICES</span>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {company.description}
            </p>
            <div className="flex items-center gap-2 text-martimma-yellow text-sm font-medium">
              <span className="w-2 h-2 rounded-full bg-martimma-yellow animate-pulse" />
              {company.tagline}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-6">Quick Links</h3>
            <nav className="flex flex-col gap-3" aria-label="Footer navigation">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-400 hover:text-martimma-yellow transition-colors text-sm flex items-center gap-2 group"
                >
                  <span className="w-0 group-hover:w-2 h-px bg-martimma-yellow transition-all duration-300" />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold mb-6">Our Services</h3>
            <div className="flex flex-col gap-3">
              {['Petroleum Products', 'Construction', 'Agriculture', 'Haulage & Logistics'].map((service) => (
                <Link
                  key={service}
                  to="/services"
                  className="text-gray-400 hover:text-martimma-yellow transition-colors text-sm"
                >
                  {service}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-6">Contact Us</h3>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-martimma-red shrink-0 mt-0.5" />
                <div className="text-gray-400 text-sm">
                  <p className="text-white font-medium">{company.offices[0].type}</p>
                  <p>{company.offices[0].address}</p>
                  <p>{company.offices[0].city}, {company.offices[0].state}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-martimma-red shrink-0" />
                <a href={`tel:${company.contact.phone}`} className="text-gray-400 hover:text-martimma-yellow transition-colors text-sm">
                  {company.contact.phone}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-martimma-red shrink-0" />
                <a href={`mailto:${company.contact.email}`} className="text-gray-400 hover:text-martimma-yellow transition-colors text-sm">
                  {company.contact.email}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">
            &copy; {currentYear} {company.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a 
              href={company.social.linkedin} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-martimma-yellow transition-colors text-sm flex items-center gap-1"
            >
              LinkedIn <ExternalLink className="w-3 h-3" />
            </a>
            <a 
              href={company.social.twitter} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-martimma-yellow transition-colors text-sm flex items-center gap-1"
            >
              Twitter <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
